import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Download as DownloadIcon,
  FileJson,
  FileCode2,
  FileSignature,
  FileKey2,
  Lock,
  Fingerprint,
  Clock,
  Files,
  CheckCircle2,
  Circle,
  Globe,
} from "lucide-react";
import { downloadWithAuth, profileLabel } from "../utils/api";

const TABS = [
  { key: "pipelines", label: "CI/CD Pipelines", route: "/integration" },
  { key: "ota", label: "OTA Deployment", route: "/ota-deployment" },
  { key: "downloads", label: "Downloads", route: "/downloads" },
  { key: "api", label: "REST API", route: "/rest-api" },
];

const FILE_TYPE_STYLES = {
  Binary: { bg: "#EEE9FE", color: "#6610F2", icon: FileCode2 },
  JSON: { bg: "#DBEAFE", color: "#2563EB", icon: FileJson },
  Signature: { bg: "#FEF3C7", color: "#B45309", icon: FileSignature },
  Key: { bg: "#DCFCE7", color: "#16A34A", icon: FileKey2 },
};

// The signature and public-key file formats depend on the Digital ID's
// profile: raw bytes for post-quantum, PEM for ECDSA, JSON for hybrid.
const SIGNATURE_NAMES = { hybrid: "signature.json" };
const PUBLIC_KEY_NAMES = { hybrid: "public_key.json", pq: "public_key.bin" };

const fileNamesFor = (identityType) => {
  const type = String(identityType || "").toLowerCase();
  return {
    signature: SIGNATURE_NAMES[type] || "signature.sig",
    publicKey: PUBLIC_KEY_NAMES[type] || "public_key.pem",
  };
};

// Only files this backend actually generates.
const buildFiles = ({ artifactId, manifest, fileName, names }) => [
  {
    name: `signed_${fileName}`,
    type: "Binary",
    size: `${(manifest.firmware.file_size / 1024 / 1024).toFixed(2)} MB`,
    description: "Signed artifact (original bytes + signature trailer)",
    path: `/api/artifact/download/${artifactId}`,
  },
  {
    name: "manifest.json",
    type: "JSON",
    size: "—",
    description: "Manifest exactly as hashed and signed",
    path: `/api/artifact/download/manifest/${artifactId}`,
  },
  {
    name: names.signature,
    type: "Signature",
    size: "—",
    description: "Detached signature",
    path: `/api/artifact/download/signature/${artifactId}`,
  },
  {
    name: names.publicKey,
    type: "Key",
    size: "—",
    description: "Public key that verifies this signature",
    path: `/api/artifact/download/public-key/${artifactId}`,
  },
];

const buildPackageSummary = ({
  fileName, selectedMethod, selectedDigitalId, algorithm, hashAlgorithm,
  signedAt, ipfsCid, ipfsUrl, fileCount,
}) => [
  { icon: FileCode2, label: "Firmware", value: fileName || "—" },
  { icon: Fingerprint, label: "Identity Source", value: selectedMethod?.title || "—" },
  { icon: Lock, label: "Algorithm", value: algorithm || "—" },
  { icon: Lock, label: "Signing Profile", value: selectedDigitalId ? profileLabel(selectedDigitalId.identity_type) : "—" },
  { icon: Lock, label: "Hash Algorithm", value: hashAlgorithm || "—" },
  { icon: Clock, label: "Signed On", value: signedAt ? new Date(signedAt).toLocaleString() : "—" },
  ...(ipfsCid
    ? [{
        icon: Globe,
        label: "IPFS CID (signed file)",
        value: (
          <a href={ipfsUrl} target="_blank" rel="noopener noreferrer"
             className="text-blue-600 underline break-all">
            {ipfsCid}
          </a>
        ),
      }]
    : []),
  { icon: Files, label: "File Count", value: `${fileCount} files` },
];

// Only claims backed by real data: the verification result when there is
// one, otherwise what the signing response itself recorded.
const triNote = (value) => (value == null ? "not requested" : undefined);

const buildIntegrity = ({ verification, manifest, signResult }) => {
  if (!verification) {
    return [
      { label: "Artifact Hash Recorded", ok: !!manifest?.firmware?.sha256 },
      { label: "Signature Created", ok: !!signResult },
      { label: "Verified In This Session", ok: false, note: "not run" },
    ];
  }
  return [
    { label: "Artifact Unchanged Since Signing", ok: verification.hash_match === true },
    { label: "Signature Valid", ok: verification.signature_valid === true },
    { label: "Manifest Bound To Signature", ok: verification.manifest_valid === true },
    { label: "Internal Timestamp", ok: verification.timestamp_valid === true, note: triNote(verification.timestamp_valid) },
    { label: "Internal Ledger Entry", ok: verification.blockchain_valid === true, note: triNote(verification.blockchain_valid) },
    { label: "Certificate Record", ok: verification.certificate_valid === true, note: triNote(verification.certificate_valid) },
  ];
};

// ---------------------------------------------------------------------
// Presentational components
// ---------------------------------------------------------------------
const TabBar = ({ activeTab, onSelect }) => (
  <div className="flex items-center gap-6 border-b border-gray-200 mb-4">
    {TABS.map((tab) => {
      const active = activeTab === tab.key;
      return (
        <button
          key={tab.key}
          onClick={() => onSelect(tab)}
          className="pb-2 text-[13px] font-medium relative"
          style={{ color: active ? "#6610F2" : "#6B7280" }}
        >
          {tab.label}
          {active && (
            <span style={{ position: "absolute", left: 0, right: 0, bottom: -1, height: 2, background: "#6610F2", borderRadius: 2 }} />
          )}
        </button>
      );
    })}
  </div>
);

const FileRow = ({ file, checked, onToggle, onDownload }) => {
  const style = FILE_TYPE_STYLES[file.type];
  const Icon = style.icon;
  return (
    <tr className="border-b border-gray-100 last:border-0">
      <td className="py-1.5 pr-2">
        <input type="checkbox" checked={checked} onChange={() => onToggle(file.name)} />
      </td>
      <td className="py-1.5 pr-2">
        <div className="flex items-center gap-1.5">
          <Icon size={14} color={style.color} />
          <span className="text-gray-800">{file.name}</span>
        </div>
      </td>
      <td className="py-1.5 pr-2">
        <span className="px-1.5 py-0.5 rounded-md text-[10.5px] font-medium" style={{ background: style.bg, color: style.color }}>
          {file.type}
        </span>
      </td>
      <td className="py-1.5 pr-2 text-gray-600">{file.size}</td>
      <td className="py-1.5 pr-2 text-gray-500">{file.description}</td>
      <td className="py-1.5 pr-2">
        <div className="flex items-center justify-end gap-2.5 text-gray-400">
          <button title="Download" className="hover:text-gray-700" onClick={() => onDownload(file)}>
            <DownloadIcon size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
};

const PackageSummaryCard = ({ rows }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <h3 className="text-[13px] font-semibold text-gray-900 mb-3">Package Summary</h3>
    <div className="flex flex-col gap-3">
      {rows.map(({ icon: Icon, label, value }) => (
        <div key={label} className="flex items-start gap-2.5">
          <Icon size={14} className="mt-0.5 text-gray-400" />
          <div className="min-w-0">
            <div className="text-[11px] text-gray-500">{label}</div>
            <div className="text-[12.5px] text-gray-800 break-words">{value}</div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const StatusCard = ({ items }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <h3 className="text-[13px] font-semibold text-gray-900">Status</h3>
    <p className="text-[11px] text-gray-500 mb-2.5">Only checks this flow actually performs.</p>
    <div className="flex flex-col gap-1.5">
      {items.map((item) => (
        <div key={item.label} className="flex items-center justify-between text-[12.5px]">
          <div className="flex items-center gap-1.5 text-gray-600">
            {item.ok ? <CheckCircle2 size={13} color="#16A34A" /> : <Circle size={13} color="#D1D5DB" />}
            {item.label}
          </div>
          <span className="text-[11px] font-medium" style={{ color: item.ok ? "#16A34A" : "#9CA3AF" }}>
            {item.note || (item.ok ? "Yes" : "No")}
          </span>
        </div>
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------
const Download = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("downloads");
  const [selected, setSelected] = useState([]);
  const [downloadError, setDownloadError] = useState("");

  const {
    artifactId,
    manifest,
    selectedMethod,
    selectedDigitalId,
    algorithm,
    hashAlgorithm,
    signedAt,
    ipfsCid,
    ipfsUrl,
    signResult,
    verification,
  } = location.state || {};

  const hasData = !!manifest && !!artifactId;
  const fileName = manifest?.firmware?.file_name || "";

  const FILES = hasData
    ? buildFiles({ artifactId, manifest, fileName, names: fileNamesFor(selectedDigitalId?.identity_type) })
    : [];

  const PACKAGE_SUMMARY = buildPackageSummary({
    fileName, selectedMethod, selectedDigitalId, algorithm, hashAlgorithm,
    signedAt, ipfsCid, ipfsUrl, fileCount: FILES.length,
  });

  const FILE_INTEGRITY = buildIntegrity({ verification, manifest, signResult });

  const allSelected = selected.length === FILES.length && FILES.length > 0;

  const toggleAll = () => setSelected(allSelected ? [] : FILES.map((f) => f.name));
  const toggleOne = (name) =>
    setSelected((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));

  const selectTab = (tab) => {
    setActiveTab(tab.key);
    navigate(tab.route, { state: location.state });
  };

  const downloadFile = async (file) => {
    setDownloadError("");
    try {
      await downloadWithAuth(file.path, file.name);
    } catch (err) {
      setDownloadError(err.message);
    }
  };

  // One after another: browsers block several simultaneous downloads.
  const downloadMany = async (files) => {
    for (const file of files) {
      await downloadFile(file);
    }
  };

  const downloadAll = () => downloadMany(FILES);
  const downloadSelected = () => downloadMany(FILES.filter((f) => selected.includes(f.name)));

  return (
    <div className="text-[13px]">
      <TabBar activeTab={activeTab} onSelect={selectTab} />

      {!hasData && (
        <div className="text-center py-16 text-gray-400 text-sm">
          No signed artifact data found. Sign an artifact first, then come back here.
        </div>
      )}

      {hasData && (
        <div className="flex gap-4 items-start">
          {/* Main column */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-[14px] font-semibold text-gray-900">Download Center</h2>
                  <p className="text-[12px] text-gray-500 mt-0.5">
                    Files actually generated by signing this artifact.
                  </p>
                </div>
                <button
                  onClick={downloadAll}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[12px] font-medium"
                  style={{ borderColor: "#6610F2", color: "#6610F2" }}
                >
                  <DownloadIcon size={14} />
                  Download All
                </button>
              </div>

              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-[13px] font-semibold text-gray-900">Generated Files</h3>
                  <p className="text-[11px] text-gray-500">Select individual files to download.</p>
                </div>
                <label className="flex items-center gap-1.5 text-[11px] text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                  Select All
                </label>
              </div>

              {downloadError && (
                <p className="text-[12.5px] text-red-600 mb-2">{downloadError}</p>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead>
                    <tr className="text-left text-[10.5px] tracking-wide text-gray-500 border-b border-gray-200">
                      <th className="py-1.5 pr-2 font-medium w-7"></th>
                      <th className="py-1.5 pr-2 font-medium">File Name</th>
                      <th className="py-1.5 pr-2 font-medium">Type</th>
                      <th className="py-1.5 pr-2 font-medium">Size</th>
                      <th className="py-1.5 pr-2 font-medium">Description</th>
                      <th className="py-1.5 pr-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FILES.map((file) => (
                      <FileRow
                        key={file.name}
                        file={file}
                        checked={selected.includes(file.name)}
                        onToggle={toggleOne}
                        onDownload={downloadFile}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {selected.length > 0 && (
                <button
                  onClick={downloadSelected}
                  className="mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[12px] font-medium"
                  style={{ borderColor: "#6610F2", color: "#6610F2" }}
                >
                  <DownloadIcon size={14} />
                  Download Selected ({selected.length})
                </button>
              )}
            </div>

            <div className="flex items-center justify-between mt-3">
              <button onClick={() => navigate(-1)} className="px-3.5 py-1.5 rounded-lg border border-gray-300 text-[12.5px] font-medium text-gray-700">
                Back
              </button>
              <button
                onClick={() => navigate("/upload-artifact")}
                className="px-3.5 py-1.5 rounded-lg text-[12.5px] font-medium text-white"
                style={{ background: "#6610F2" }}
              >
                Go to Dashboard →
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-72 flex-shrink-0 flex flex-col gap-3">
            <PackageSummaryCard rows={PACKAGE_SUMMARY} />
            <StatusCard items={FILE_INTEGRITY} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Download;